import type { EdgeModifier } from "building-types/modifiers/edgeModifierTypes";
import type { GeneratedModifier } from "building-types/modifiers/generatedModifier";
import type { RWTable, Table } from "common/data/table";
import { polyLine2Copy, polyLine2IsClosed, polyLine2MoveVertices } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

export function getEntityEdgeModifiersTable(edgeModifiers: Table<EdgeModifier>, entityId: string) {
  const roomEdgeModifiers: RWTable<EdgeModifier> = {};
  for (const key in edgeModifiers) {
    const edgeModifier = edgeModifiers[key];
    if (edgeModifier.parentId !== entityId) {
      continue;
    }
    roomEdgeModifiers[key] = edgeModifiers[key];
  }
  return roomEdgeModifiers;
}

export function getEdgeModifierIdsOfModifierId(edgeModifiers: Table<EdgeModifier>, modifierId: string): string[] {
  const ids: string[] = [];
  for (const edgeModifierId in edgeModifiers) {
    const edgeModifier = edgeModifiers[edgeModifierId];
    if (edgeModifier.modifierId !== modifierId) {
      continue;
    }
    ids.push(edgeModifierId);
  }
  return ids;
}

export function getEdgeModifierIdsOfParentIdAndModifierId(
  edgeModifiers: Table<EdgeModifier>,
  parentId: string,
  modifierId: string,
): string[] {
  const ids: string[] = [];
  for (const edgeModifierId in edgeModifiers) {
    const edgeModifier = edgeModifiers[edgeModifierId];
    if (edgeModifier.parentId !== parentId || edgeModifier.modifierId !== modifierId) {
      continue;
    }
    ids.push(edgeModifierId);
  }
  return ids;
}

export function getEdgeModifierIdsOfParentIdAndModifierIdAndEdgeId(
  edgeModifiers: Table<EdgeModifier>,
  parentId: string | null,
  modifierId: string,
  edgeId: string,
): string[] {
  const ids: string[] = [];
  for (const edgeModifierId in edgeModifiers) {
    const edgeModifier = edgeModifiers[edgeModifierId];
    if (
      edgeModifier.parentId !== parentId ||
      edgeModifier.modifierId !== modifierId ||
      edgeModifier.edgeId !== edgeId
    ) {
      continue;
    }
    ids.push(edgeModifierId);
  }
  return ids;
}

export function polyLine2Modify(
  outline: PolyLine2,
  edgeModifiers: Table<EdgeModifier>,
  modifiers: Table<GeneratedModifier>,
  edgeIds: string[],
) {
  if (!edgeModifiers || !modifiers) {
    return outline;
  }
  const numVertices = polyLine2IsClosed(outline) ? edgeIds.length : edgeIds.length + 1;
  let mutableOutline = polyLine2Copy(outline);
  const edgeVertexHashTable: { [hash: string]: boolean } = {};

  for (const edgeModifierId in edgeModifiers) {
    const edgeModifier = edgeModifiers[edgeModifierId];
    if (!edgeModifier) {
      continue;
    }

    const modifier = modifiers[edgeModifier.modifierId];
    if (!modifier) {
      continue;
    }

    const edgeIndex = edgeIds.indexOf(edgeModifier.edgeId);
    if (edgeIndex < 0) {
      continue;
    }

    const vertexStartHash = edgeIndex + "_" + edgeModifier.modifierId;
    const vertexEndHash = ((edgeIndex + 1) % numVertices) + "_" + edgeModifier.modifierId;

    if (!edgeVertexHashTable[vertexStartHash]) {
      edgeVertexHashTable[vertexStartHash] = true;

      mutableOutline = polyLine2MoveVertices(mutableOutline, [edgeIndex], modifier.moveX, modifier.moveY);
    }

    if (!edgeVertexHashTable[vertexEndHash]) {
      edgeVertexHashTable[vertexEndHash] = true;
      mutableOutline = polyLine2MoveVertices(
        mutableOutline,
        [(edgeIndex + 1) % numVertices],
        modifier.moveX,
        modifier.moveY,
      );
    }
  }
  return mutableOutline;
}

/**
 *
 * @param outline room outline
 * @param edgeModifiers filtered edge modifiers belonging to the room
 * @param edgeIds edge ids belonging to the room
 * @param moveX distance to move in x direction
 * @param moveY distance to move in y direction
 * @returns
 */
export function mutateRoomOutlineUsingEdgeModifiers(
  outline: PolyLine2,
  edgeModifiers: EdgeModifier[],
  edgeIds: string[],
  moveX: number,
  moveY: number,
) {
  if (edgeModifiers.length === 0) {
    return outline;
  }
  const numVertices = polyLine2IsClosed(outline) ? edgeIds.length : edgeIds.length + 1;
  let mutatedOutline = polyLine2Copy(outline);
  const edgeVertexHashTable: { [hash: string]: boolean } = {};

  for (const edgeModifierId of Object.keys(edgeModifiers)) {
    const edgeModifier = edgeModifiers[edgeModifierId];
    if (!edgeModifier) {
      continue;
    }

    const edgeIndex = edgeIds.indexOf(edgeModifier.edgeId);
    if (edgeIndex < 0) {
      continue;
    }

    const vertexStartHash = edgeIndex + "_" + edgeModifier.modifierId;
    const vertexEndHash = ((edgeIndex + 1) % numVertices) + "_" + edgeModifier.modifierId;

    if (!edgeVertexHashTable[vertexStartHash]) {
      edgeVertexHashTable[vertexStartHash] = true;

      mutatedOutline = polyLine2MoveVertices(mutatedOutline, [edgeIndex], moveX, moveY);
    }

    if (!edgeVertexHashTable[vertexEndHash]) {
      edgeVertexHashTable[vertexEndHash] = true;
      mutatedOutline = polyLine2MoveVertices(mutatedOutline, [(edgeIndex + 1) % numVertices], moveX, moveY);
    }
  }
  return mutatedOutline;
}
